import { dayjs, formatPercentage } from '@/utilities';

import {
    createFinancialFormula as financialFormula,
    financialFormulaOperand as formulaOperand,
    financialFormulaOperator as formulaOperator,
} from './components/FinancialMetricFormula/formula';
import {
    FinancialOverviewConfiguration,
    FinancialOverviewEntityKind,
    FinancialOverviewMetricContext,
    FinancialOverviewMetricDefinition,
    FinancialOverviewSectionKey,
    FinancialOverviewSecurityTypeOptions,
    FinancialOverviewTickerDataPoint,
} from './types';

export const OVERVIEW_SECTION_OPTIONS: Array<{ key: FinancialOverviewSectionKey; label: string }> = [
    { key: 'chart', label: 'Price Chart' },
    { key: 'keyMetrics', label: 'Financial Metrics' },
    { key: 'sectorKpis', label: 'Sector KPIs' },
    { key: 'industryKpis', label: 'Industry KPIs' },
    { key: 'map', label: 'Map' },
];

export const OVERLAY_SERIES_OPTIONS = [
    { label: 'Comparable Companies', value: 'comparables' },
    { label: 'Indices', value: 'indices' },
    { label: 'Funds', value: 'funds' },
    { label: 'Economic Data', value: 'economicData' },
];

export const getDateRange = (segment: string) => {
    const today = dayjs().format('YYYY-MM-DD');
    let fromDate;

    switch (segment) {
        case 'ALL':
        case 'Max':
            fromDate = dayjs('1970-01-01').format('YYYY-MM-DD');
            break;
        case '10Y':
            fromDate = dayjs().subtract(10, 'years').format('YYYY-MM-DD');
            break;
        case '5Y':
            fromDate = dayjs().subtract(5, 'years').format('YYYY-MM-DD');
            break;
        case '2Y':
            fromDate = dayjs().subtract(2, 'years').format('YYYY-MM-DD');
            break;
        case '1Y':
            fromDate = dayjs().subtract(1, 'year').format('YYYY-MM-DD');
            break;
        case 'YTD':
            fromDate = dayjs().startOf('year').format('YYYY-MM-DD');
            break;
        case '6M':
            fromDate = dayjs().subtract(6, 'months').format('YYYY-MM-DD');
            break;
        case '1Q':
            fromDate = dayjs().subtract(1, 'quarter').format('YYYY-MM-DD');
            break;
        case 'QTD':
            fromDate = dayjs().startOf('quarter').format('YYYY-MM-DD');
            break;
        case '1M':
            fromDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
            break;
        case '1W':
            fromDate = dayjs().subtract(1, 'week').format('YYYY-MM-DD');
            break;
        case '5D':
            fromDate = dayjs().subtract(5, 'days').format('YYYY-MM-DD');
            break;
        case '1D':
            fromDate = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
            break;
        case 'realTime':
            fromDate = today;
            break;
        default:
            fromDate = today;
    }

    return { to: today, from: fromDate };
};

const formatOptionalPercentage = (value?: number | string | null) =>
    value || value === 0 ? formatPercentage(Number(value)) : '-';

const formatOptionalMultiple = (value?: number | string | null) =>
    value || value === 0
        ? `${Number(value).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}x`
        : '-';

export const formatOptionalNumber = (value?: number | string | null) =>
    value || value === 0 ? Number(value).toLocaleString('en-US') : '-';

const renderOptionalFinancialValue = (
    context: FinancialOverviewMetricContext,
    value: number | string | null | undefined,
    options?: { kind?: 'currency' | 'number'; scalable?: boolean },
) => (value || value === 0 ? context.renderFinancialValue(value, options) : '-');

export const resolveLatestFinancialOverviewMarketPrice = (data: FinancialOverviewTickerDataPoint[]) => {
    const latestPrice = [...data]
        .reverse()
        .map((point) => point.close)
        .filter((price) => price !== undefined && price !== null && price !== '')
        .map(Number)
        .find((price) => Number.isFinite(price));

    return latestPrice;
};

export const getFinancialOverviewSecurityTypeLabel = ({
    entityKind,
    isEtf = false,
}: FinancialOverviewSecurityTypeOptions) => {
    if (entityKind === 'privateCompany') {
        return 'Private Entity';
    }

    if (entityKind === 'privateFund') {
        return 'Private Fund';
    }

    if (entityKind === 'publicFund') {
        return isEtf ? 'ETF' : 'Fund';
    }

    return 'Public Company';
};

export const FINANCIAL_OVERVIEW_METRICS: FinancialOverviewMetricDefinition[] = [
    {
        key: 'stockPrice',
        label: 'Market Price per Share',
        description: 'The latest available trading price for one share of the selected security.',
        getValue: (allData, context) =>
            renderOptionalFinancialValue(context, context.marketPrice ?? allData?.enterpriseValues?.stockPrice, {
                scalable: false,
            }),
    },
    {
        key: 'sharesOutstanding',
        label: 'Shares Outstanding',
        description: 'The total number of company shares currently held by investors.',
        getValue: (allData, context) =>
            renderOptionalFinancialValue(context, allData?.enterpriseValues?.numberOfShares, { kind: 'number' }),
    },
    {
        key: 'marketCap',
        label: 'Market Capitalization',
        description: 'The market’s aggregate valuation of the company’s outstanding common equity.',
        formula: financialFormula(
            formulaOperand('stockPrice', 'Market Price per Share', 'market'),
            formulaOperator('×'),
            formulaOperand('sharesOutstanding', 'Shares Outstanding', 'market'),
        ),
        getValue: (allData, context) => renderOptionalFinancialValue(context, allData?.keyMetrics?.marketCap),
    },
    {
        key: 'enterpriseValue',
        label: 'Enterprise Value',
        description: 'The value attributed to the company’s core operations across its capital providers.',
        formula: financialFormula(
            formulaOperand('marketCap', 'Market Capitalization', 'valuation'),
            formulaOperator('+'),
            formulaOperand('totalDebtAndPreferred', 'Debt + Preferred Interests', 'balance-sheet'),
            formulaOperator('−'),
            formulaOperand('cashAndEquivalents', 'Cash + Equivalents', 'balance-sheet'),
        ),
        getValue: (allData, context) => renderOptionalFinancialValue(context, allData?.keyMetrics?.enterpriseValue),
    },
    {
        key: 'cashAndEquivalents',
        label: 'Cash + Equivalents',
        description: 'Cash and highly liquid short-term holdings readily available to the company.',
        getValue: (allData, context) =>
            renderOptionalFinancialValue(context, allData?.balanceSheet?.cashAndCashEquivalents),
    },
    {
        key: 'totalDebt',
        label: 'Total Debt',
        description: 'The combined current and long-term interest-bearing obligations reported by the company.',
        getValue: (allData, context) => renderOptionalFinancialValue(context, allData?.balanceSheet?.totalDebt),
    },
    {
        key: 'revenue',
        label: 'Revenue',
        description: 'Total income generated from the company’s primary operations before expenses.',
        getValue: (allData, context) => renderOptionalFinancialValue(context, allData?.incomeStatement?.revenue),
    },
    {
        key: 'revenuePerShare',
        label: 'Revenue per Share',
        description: 'Reported revenue allocated across the company’s outstanding shares.',
        formula: financialFormula(
            formulaOperand('revenue', 'Revenue', 'income-statement'),
            formulaOperator('÷'),
            formulaOperand('sharesOutstanding', 'Shares Outstanding', 'market'),
        ),
        getValue: (allData, context) =>
            renderOptionalFinancialValue(context, allData?.keyMetrics?.revenuePerShare, { scalable: false }),
    },
    {
        key: 'netIncomePerShare',
        label: 'Net Income per Share',
        description: 'Net income attributable to each outstanding share after all expenses and taxes.',
        formula: financialFormula(
            formulaOperand('netIncome', 'Net Income', 'income-statement'),
            formulaOperator('÷'),
            formulaOperand('sharesOutstanding', 'Shares Outstanding', 'market'),
        ),
        getValue: (allData, context) =>
            renderOptionalFinancialValue(context, allData?.keyMetrics?.netIncomePerShare, { scalable: false }),
    },
    {
        key: 'netProfitMargin',
        label: 'Net Profit Margin',
        description: 'The share of revenue retained as profit after all expenses and taxes.',
        formula: financialFormula(
            formulaOperand('netIncome', 'Net Income', 'income-statement'),
            formulaOperator('÷'),
            formulaOperand('revenue', 'Revenue', 'income-statement'),
        ),
        getValue: (allData) => formatOptionalPercentage(allData?.ratios?.netProfitMargin),
    },
    {
        key: 'returnOnAssets',
        label: 'Return on Assets',
        description: 'How efficiently the company uses its asset base to generate profit.',
        formula: financialFormula(
            formulaOperand('netIncome', 'Net Income', 'income-statement'),
            formulaOperator('÷'),
            formulaOperand('averageAssets', 'Average Total Assets', 'balance-sheet'),
        ),
        getValue: (allData) => formatOptionalPercentage(allData?.ratios?.returnOnAssets),
    },
    {
        key: 'earningsYield',
        label: 'Earnings Yield',
        description: 'The earnings generated for each dollar invested at the current market price.',
        formula: financialFormula(
            formulaOperand('earningsPerShare', 'Earnings per Share', 'income-statement'),
            formulaOperator('÷'),
            formulaOperand('stockPrice', 'Market Price per Share', 'market'),
        ),
        getValue: (allData) => formatOptionalPercentage(allData?.keyMetrics?.earningsYield),
    },
    {
        key: 'dividendYield',
        label: 'Dividend Yield',
        description: 'The annual dividend income represented by each dollar invested at the current share price.',
        formula: financialFormula(
            formulaOperand('annualDividendsPerShare', 'Annual Dividends per Share', 'cash-flow'),
            formulaOperator('÷'),
            formulaOperand('stockPrice', 'Market Price per Share', 'market'),
        ),
        getValue: (allData) => formatOptionalPercentage(allData?.keyMetrics?.dividendYield),
    },
    {
        key: 'peRatio',
        label: 'P / E Ratio',
        description: 'How much investors are paying for each unit of the company’s earnings.',
        formula: financialFormula(
            formulaOperand('stockPrice', 'Market Price per Share', 'market'),
            formulaOperator('÷'),
            formulaOperand('earningsPerShare', 'Earnings per Share', 'income-statement'),
        ),
        getValue: (allData) => formatOptionalMultiple(allData?.keyMetrics?.peRatio),
    },
    {
        key: 'pbRatio',
        label: 'P / B Ratio',
        description: 'How much investors are paying for each unit of the company’s reported book value.',
        formula: financialFormula(
            formulaOperand('stockPrice', 'Market Price per Share', 'market'),
            formulaOperator('÷'),
            formulaOperand('bookValuePerShare', 'Book Value per Share', 'balance-sheet'),
        ),
        getValue: (allData) => formatOptionalMultiple(allData?.keyMetrics?.pbRatio),
    },
    {
        key: 'bookValuePerShare',
        label: 'Book Value per Share',
        description: 'Common shareholders’ equity allocated across outstanding shares.',
        formula: financialFormula(
            formulaOperand('commonEquity', 'Common Shareholders’ Equity', 'balance-sheet'),
            formulaOperator('÷'),
            formulaOperand('sharesOutstanding', 'Shares Outstanding', 'market'),
        ),
        getValue: (allData, context) =>
            renderOptionalFinancialValue(context, allData?.keyMetrics?.bookValuePerShare, { scalable: false }),
    },
    {
        key: 'netCurrentAssetValue',
        label: 'Net Current Asset Value (NCAV)',
        description: 'A conservative liquidation-value proxy based on near-term assets and reported liabilities.',
        formula: financialFormula(
            formulaOperand('currentAssets', 'Current Assets', 'balance-sheet'),
            formulaOperator('−'),
            formulaOperand('totalLiabilities', 'Total Liabilities', 'balance-sheet'),
        ),
        getValue: (allData, context) =>
            renderOptionalFinancialValue(context, allData?.keyMetrics?.netCurrentAssetValue),
    },
    {
        key: 'capexPerShare',
        label: 'CapEx per Share',
        description: 'Capital expenditures allocated across the company’s outstanding shares.',
        formula: financialFormula(
            formulaOperand('capitalExpenditure', 'Capital Expenditure', 'cash-flow'),
            formulaOperator('÷'),
            formulaOperand('sharesOutstanding', 'Shares Outstanding', 'market'),
        ),
        getValue: (allData, context) =>
            renderOptionalFinancialValue(context, allData?.keyMetrics?.capexPerShare, { scalable: false }),
    },
    {
        key: 'operatingIncome',
        label: 'Operating Income',
        description: 'Profit generated by core operations before interest and taxes.',
        getValue: (allData, context) =>
            renderOptionalFinancialValue(context, allData?.incomeStatement?.operatingIncome),
    },
    {
        key: 'freeCashFlow',
        label: 'Free Cash Flow',
        description: 'Cash available after funding operations and required capital investment.',
        formula: financialFormula(
            formulaOperand('operatingCashFlow', 'Operating Cash Flow', 'cash-flow'),
            formulaOperator('−'),
            formulaOperand('capitalExpenditure', 'Capital Expenditure', 'cash-flow'),
        ),
        getValue: (allData, context) => renderOptionalFinancialValue(context, allData?.cashFlows?.freeCashFlow),
    },
    {
        key: 'workingCapital',
        label: 'Working Capital',
        description: 'Resources available to fund the company’s short-term operating needs.',
        formula: financialFormula(
            formulaOperand('currentAssets', 'Current Assets', 'balance-sheet'),
            formulaOperator('−'),
            formulaOperand('currentLiabilities', 'Current Liabilities', 'balance-sheet'),
        ),
        getValue: (allData, context) => renderOptionalFinancialValue(context, allData?.keyMetrics?.workingCapital),
    },
    {
        key: 'cashPerShare',
        label: 'Cash per Share',
        description: 'Cash and equivalents allocated across the company’s outstanding shares.',
        formula: financialFormula(
            formulaOperand('cashAndEquivalents', 'Cash + Equivalents', 'balance-sheet'),
            formulaOperator('÷'),
            formulaOperand('sharesOutstanding', 'Shares Outstanding', 'market'),
        ),
        getValue: (allData, context) =>
            renderOptionalFinancialValue(context, allData?.ratios?.cashPerShare, { scalable: false }),
    },
    {
        key: 'evToSales',
        label: 'EV / Sales',
        description: 'How the company’s operating value compares with the revenue it generates.',
        formula: financialFormula(
            formulaOperand('enterpriseValue', 'Enterprise Value', 'valuation'),
            formulaOperator('÷'),
            formulaOperand('revenue', 'Revenue', 'income-statement'),
        ),
        getValue: (allData) => formatOptionalMultiple(allData?.keyMetrics?.evToSales),
    },
    {
        key: 'evToFreeCashFlow',
        label: 'EV / FCF',
        description: 'How the company’s operating value compares with the free cash flow it generates.',
        formula: financialFormula(
            formulaOperand('enterpriseValue', 'Enterprise Value', 'valuation'),
            formulaOperator('÷'),
            formulaOperand('freeCashFlow', 'Free Cash Flow', 'cash-flow'),
        ),
        getValue: (allData) => formatOptionalMultiple(allData?.keyMetrics?.evToFreeCashFlow),
    },
    {
        key: 'incomeTaxExpense',
        label: 'Income Tax Expense',
        description: 'The reported tax cost associated with income for the period.',
        getValue: (allData, context) =>
            renderOptionalFinancialValue(context, allData?.incomeStatement?.incomeTaxExpense),
    },
    {
        key: 'roe',
        label: 'ROE',
        description: 'How effectively the company converts shareholder equity capital into profit.',
        formula: financialFormula(
            formulaOperand('netIncome', 'Net Income', 'income-statement'),
            formulaOperator('÷'),
            formulaOperand('averageEquity', 'Average Shareholders’ Equity', 'balance-sheet'),
        ),
        getValue: (allData) => formatOptionalPercentage(allData?.keyMetrics?.roe),
    },
    {
        key: 'institutionalOwnership',
        label: '% Owned by Institutions',
        description: 'The percentage of outstanding shares reported as held by institutional investors.',
        getValue: (_allData, { institutionalStats }) => {
            const ownershipPercent = institutionalStats?.ownershipPercent;

            return ownershipPercent || ownershipPercent === 0
                ? `${Number(ownershipPercent).toLocaleString('en-US', {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                  })}%`
                : '-';
        },
    },
    {
        key: 'averageReceivables',
        label: 'Average Receivables',
        description: 'The average customer balances owed to the company across the measurement period.',
        formula: financialFormula(
            formulaOperator('('),
            formulaOperand('openingReceivables', 'Opening Receivables', 'balance-sheet'),
            formulaOperator('+'),
            formulaOperand('closingReceivables', 'Closing Receivables', 'balance-sheet'),
            formulaOperator(')'),
            formulaOperator('÷'),
            formulaOperand('periodCount', '2', 'constant'),
        ),
        getValue: (allData, context) => renderOptionalFinancialValue(context, allData?.keyMetrics?.averageReceivables),
    },
    {
        key: 'averagePayables',
        label: 'Average Payables',
        description: 'The average supplier and vendor balances owed by the company across the measurement period.',
        formula: financialFormula(
            formulaOperator('('),
            formulaOperand('openingPayables', 'Opening Payables', 'balance-sheet'),
            formulaOperator('+'),
            formulaOperand('closingPayables', 'Closing Payables', 'balance-sheet'),
            formulaOperator(')'),
            formulaOperator('÷'),
            formulaOperand('periodCount', '2', 'constant'),
        ),
        getValue: (allData, context) => renderOptionalFinancialValue(context, allData?.keyMetrics?.averagePayables),
    },
    {
        key: 'debtToEquity',
        label: 'Debt / Equity',
        description: 'The company’s use of debt financing relative to its shareholder-funded capital.',
        formula: financialFormula(
            formulaOperand('totalDebt', 'Total Debt', 'balance-sheet'),
            formulaOperator('÷'),
            formulaOperand('shareholdersEquity', 'Shareholders’ Equity', 'balance-sheet'),
        ),
        getValue: (allData) => formatOptionalMultiple(allData?.keyMetrics?.debtToEquity),
    },
    {
        key: 'roic',
        label: 'ROIC',
        description: 'How efficiently the business converts invested capital into after-tax operating profit.',
        formula: financialFormula(
            formulaOperand('nopat', 'After-Tax Operating Profit', 'income-statement'),
            formulaOperator('÷'),
            formulaOperand('investedCapital', 'Invested Capital', 'balance-sheet'),
        ),
        getValue: (allData) => formatOptionalPercentage(allData?.keyMetrics?.roic),
    },
    {
        key: 'goodwill',
        label: 'Goodwill',
        description: 'The acquisition premium recorded above the fair value of identifiable net assets.',
        getValue: (allData, context) => renderOptionalFinancialValue(context, allData?.balanceSheet?.goodwill),
    },
    {
        key: 'operatingExpenses',
        label: 'Operating Expenses',
        description: 'Costs incurred to run core operations, excluding non-operating items.',
        getValue: (allData, context) =>
            renderOptionalFinancialValue(context, allData?.incomeStatement?.operatingExpenses),
    },
];

const FINANCIAL_OVERVIEW_METRIC_MAP = new Map(
    FINANCIAL_OVERVIEW_METRICS.map((metric) => [metric.key, metric] as const),
);

const DEFAULT_DETAIL_METRIC_KEYS = [
    'cashAndEquivalents',
    'totalDebt',
    'revenue',
    'revenuePerShare',
    'netIncomePerShare',
    'netProfitMargin',
    'returnOnAssets',
    'earningsYield',
    'dividendYield',
    'peRatio',
    'pbRatio',
    'bookValuePerShare',
    'netCurrentAssetValue',
    'capexPerShare',
    'operatingIncome',
    'freeCashFlow',
    'workingCapital',
    'cashPerShare',
    'evToSales',
    'evToFreeCashFlow',
    'incomeTaxExpense',
    'roe',
    'institutionalOwnership',
    'averageReceivables',
    'averagePayables',
    'debtToEquity',
    'roic',
    'goodwill',
    'operatingExpenses',
];

export const getDefaultOverviewConfiguration = (
    entityKind: FinancialOverviewEntityKind = 'publicCompany',
): FinancialOverviewConfiguration => {
    const summaryMetricKeysByEntityKind: Record<FinancialOverviewEntityKind, string[]> = {
        privateCompany: ['revenue', 'operatingIncome', 'freeCashFlow', 'totalDebt'],
        privateFund: ['cashAndEquivalents', 'totalDebt', 'freeCashFlow', 'debtToEquity'],
        publicCompany: ['stockPrice', 'sharesOutstanding', 'marketCap', 'enterpriseValue'],
        publicFund: ['stockPrice', 'marketCap', 'dividendYield', 'sharesOutstanding'],
    };

    return {
        detailMetricKeys: DEFAULT_DETAIL_METRIC_KEYS,
        sectionVisibility: {
            chart: entityKind === 'publicCompany' || entityKind === 'publicFund',
            industryKpis: entityKind === 'publicCompany',
            keyMetrics: true,
            map: true,
            sectorKpis: entityKind === 'publicCompany',
        },
        summaryMetricKeys: summaryMetricKeysByEntityKind[entityKind],
    };
};

export const getMetricsByKeys = (keys: string[]) =>
    keys.map((key) => FINANCIAL_OVERVIEW_METRIC_MAP.get(key)).filter(Boolean) as FinancialOverviewMetricDefinition[];
